import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface StatusDistributionChartProps {
  data: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  loading?: boolean;
}

const StatusDistributionChart: React.FC<StatusDistributionChartProps> = ({ data, loading = false }) => {
  const COLORS = {
    'closed': '#10b981',
    'fully_implemented': '#059669',
    'in_progress': '#3b82f6',
    'on_hold': '#f59e0b',
    'rejected': '#ef4444',
    'draft': '#6b7280',
    'new': '#8b5cf6',
    'risk_of_delay': '#f97316'
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Workshop Status Distribution</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Workshop Status Distribution</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No status data available
        </div>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Workshop Status Distribution</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={(entry: any) => {
                const formattedStatus = entry.status.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                const percentage = ((entry.count / total) * 100).toFixed(1);
                return `${formattedStatus}: ${percentage}%`;
              }}
              outerRadius={80}
              fill="#8884d8"
              dataKey="count"
              nameKey="status"
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={COLORS[entry.status as keyof typeof COLORS] || '#6b7280'} 
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: number, name: string, props: any) => [
                `${value} workshops (${props.payload.percentage.toFixed(1)}%)`,
                'Count'
              ]}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        {data.map((item) => (
          <div key={item.status} className="flex items-center justify-between">
            <div className="flex items-center">
              <div 
                className="w-3 h-3 rounded mr-2" 
                style={{ backgroundColor: COLORS[item.status as keyof typeof COLORS] || '#6b7280' }}
              ></div>
              <span className="capitalize">{item.status.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</span>
            </div>
            <span className="font-medium">{item.count}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 text-center text-sm text-gray-600">
        Total: {total} workshops
      </div>
    </div>
  );
};

export default StatusDistributionChart;
